"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, ReactNode, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { DETAIL_OPEN_STATE_KEY, useUpdateRouteParam } from "./DetailDialog.utils";
import DetailDialogContainer from "./DialogContainer";
import { useMobile } from "@/components/providers/MobileProvider";
import useRealEstateDetail from "@/hooks/QueryHooks/useRealEstateDetail";
import { detailPathForListing } from "../locationTrail";
import { useHoldForLoader } from "@/hooks/useHoldForLoader";
import { buildRoomFilterQuery } from "../roomFilterParams";
import { BusinessType } from "@/types/real-estate";
export interface DialogContextItem {
  slug: string;
  title: string;
}
interface DialogDetailsContextValue {
  isMobile: boolean;
  openModal: boolean;
  openModalId: string | null;
  /** List of properties available for the TitleSwitcher in the current dialog context. */
  dialogContextItems: DialogContextItem[];
  persistedModals: string[];
  handleOpenDialog: (realEstateSlug: string, contextItems?: DialogContextItem[]) => void;
  handleCloseDialog: (realEstateSlug?: string | null) => void;
  handleRestoreDialog: (realEstateSlug: string) => void;
  handleMinimizeDialog: (realEstateSlug?: string | null) => void;
  handlePersistDialog: (realEstateSlug?: string | null) => void;
  handleClosePill: (realEstateSlug?: string | null) => void;
  handleUnpersistDialogs: () => void;
  handleMaximizeDialog: () => void;
  handleOnOpenChange: (open: boolean) => void;
  handleDismissDialog: (realEstateSlug?: string | null) => void;
}
type DialogDetailsModalState = Pick<DialogDetailsContextValue, "isMobile" | "openModal" | "openModalId" | "dialogContextItems">;
type DialogDetailsPersistedState = Pick<DialogDetailsContextValue, "persistedModals">;
type DialogDetailsActions = Omit<DialogDetailsContextValue, keyof DialogDetailsModalState | keyof DialogDetailsPersistedState>;
const DialogDetailsModalStateContext = createContext<DialogDetailsModalState | null>(null);
const DialogDetailsPersistedStateContext = createContext<DialogDetailsPersistedState | null>(null);
const DialogDetailsActionsContext = createContext<DialogDetailsActions | null>(null);
const PERSISTED_MODALS_STORAGE_KEY = "persistedDetailDialogs";
export function DialogDetailsProvider(t0) {
  const $ = _c(71);
  const {
    children
  } = t0;
  const router = useRouter();
  const isMobile = useMobile();
  const searchParams = useSearchParams();
  const shouldHoldForLoader = useHoldForLoader();
  const {
    handleUpdateRouteParams
  } = useUpdateRouteParam();
  const [openModal, setOpenModal] = useState(false);
  const [openModalId, setOpenModalId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [persistedModals, setPersistedModals] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [dialogContextItems, setDialogContextItems] = useState(t2);
  let t3;
  if ($[2] !== openModalId) {
    t3 = {
      realEstateSlug: openModalId
    };
    $[2] = openModalId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: realEstateDetailData
  } = useRealEstateDetail(t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (typeof window !== "undefined") {
        ;
        try {
          const storedModals = localStorage.getItem(PERSISTED_MODALS_STORAGE_KEY);
          if (storedModals) {
            const parsedModals = JSON.parse(storedModals);
            if (Array.isArray(parsedModals)) {
              setPersistedModals(parsedModals);
            }
          }
        } catch (t6) {
          const error = t6;
          console.error("Failed to load persisted modals:", error);
        }
      }
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== persistedModals) {
    t6 = () => {
      if (typeof window !== "undefined" && persistedModals) {
        ;
        try {
          localStorage.setItem(PERSISTED_MODALS_STORAGE_KEY, JSON.stringify(persistedModals));
        } catch (t8) {
          const error_0 = t8;
          console.error("Failed to save persisted modals:", error_0);
        }
      }
    };
    t7 = [persistedModals];
    $[6] = persistedModals;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== searchParams) {
    t8 = searchParams.get("modal");
    $[9] = searchParams;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const modalParam = t8;
  let t10;
  let t9;
  if ($[11] !== isMobile || $[12] !== modalParam || $[13] !== openModal || $[14] !== openModalId || $[15] !== shouldHoldForLoader) {
    t9 = () => {
      if (modalParam !== null && !openModal) {
        if (shouldHoldForLoader) {
          return;
        }
        setOpenModal(true);
        setOpenModalId(modalParam);
      } else {
        if (modalParam !== null && openModal && modalParam !== openModalId) {
          setOpenModalId(modalParam);
        } else {
          if (modalParam === null && openModal && openModalId !== null) {
            setOpenModal(false);
            setTimeout(() => setOpenModalId(null), isMobile ? 600 : 0);
          }
        }
      }
    };
    t10 = [modalParam, isMobile, openModal, openModalId, shouldHoldForLoader];
    $[11] = isMobile;
    $[12] = modalParam;
    $[13] = openModal;
    $[14] = openModalId;
    $[15] = shouldHoldForLoader;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== handleUpdateRouteParams) {
    t11 = (realEstateSlug, contextItems) => {
      setDialogContextItems(contextItems ?? []);
      setOpenModalId(realEstateSlug);
      const alreadyOpen = typeof window !== "undefined" && window.history.state?.myStateKey === DETAIL_OPEN_STATE_KEY;
      handleUpdateRouteParams(realEstateSlug, {
        replace: alreadyOpen
      });
    };
    $[18] = handleUpdateRouteParams;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleOpenDialog = t11;
  let t12;
  if ($[20] !== handleUpdateRouteParams || $[21] !== router) {
    t12 = realEstateSlug_0 => {
      if (!realEstateSlug_0) {
        return;
      }
      const currentState = window.history.state;
      setPersistedModals(prev => prev.filter(slug => slug !== realEstateSlug_0));
      setDialogContextItems([]);
      if (currentState?.myStateKey === DETAIL_OPEN_STATE_KEY) {
        router.back();
      } else {
        handleUpdateRouteParams(null);
      }
    };
    $[20] = handleUpdateRouteParams;
    $[21] = router;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const handleCloseDialog = t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = realEstateSlug_1 => {
      if (!realEstateSlug_1) {
        return;
      }
      setPersistedModals(prev_0 => prev_0.filter(slug_0 => slug_0 !== realEstateSlug_1));
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const handleClosePill = t13;
  let t14;
  if ($[24] !== handleUpdateRouteParams || $[25] !== router) {
    t14 = realEstateSlug_2 => {
      if (!realEstateSlug_2) {
        return;
      }
      setDialogContextItems([]);
      const currentState_0 = window.history.state;
      if (currentState_0?.myStateKey === DETAIL_OPEN_STATE_KEY) {
        router.back();
      } else {
        handleUpdateRouteParams(null);
      }
    };
    $[24] = handleUpdateRouteParams;
    $[25] = router;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleDismissDialog = t14;
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = realEstateSlug_3 => {
      if (!realEstateSlug_3) {
        return;
      }
      setPersistedModals(prev_1 => prev_1.includes(realEstateSlug_3) ? prev_1 : [...prev_1, realEstateSlug_3]);
    };
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const handlePersistDialog = t15;
  let t16;
  if ($[28] !== handleUpdateRouteParams || $[29] !== router) {
    t16 = realEstateSlug_4 => {
      if (!realEstateSlug_4) {
        return;
      }
      handlePersistDialog(realEstateSlug_4);
      const currentState_1 = window.history.state;
      if (currentState_1?.myStateKey === DETAIL_OPEN_STATE_KEY) {
        router.back();
      } else {
        handleUpdateRouteParams(null);
      }
    };
    $[28] = handleUpdateRouteParams;
    $[29] = router;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleMinimizeDialog = t16;
  let t17;
  if ($[31] !== handleUpdateRouteParams) {
    t17 = realEstateSlug_5 => {
      handleUpdateRouteParams(realEstateSlug_5);
    };
    $[31] = handleUpdateRouteParams;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const handleRestoreDialog = t17;
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setPersistedModals([]);
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const handleUnpersistDialogs = t18;
  let t19;
  if ($[34] !== handleCloseDialog || $[35] !== openModalId) {
    t19 = open => {
      if (!open) {
        handleCloseDialog(openModalId);
      }
    };
    $[34] = handleCloseDialog;
    $[35] = openModalId;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const handleOnOpenChange = t19;
  let t20;
  if ($[37] !== openModalId || $[38] !== realEstateDetailData?.data?.locations) {
    t20 = () => {
      if (!openModalId) {
        return;
      }
      return detailPathForListing(realEstateDetailData?.data?.locations ?? [], openModalId);
    };
    $[37] = openModalId;
    $[38] = realEstateDetailData?.data?.locations;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const getFullRouteForDetail = t20;
  let t21;
  if ($[40] !== getFullRouteForDetail || $[41] !== realEstateDetailData?.data?.realEstate?.businessType || $[42] !== realEstateDetailData?.data?.relatedListings?.length || $[43] !== router || $[44] !== searchParams) {
    t21 = () => {
      const fullRoute = getFullRouteForDetail();
      if (!fullRoute) {
        return;
      }
      const isRoomHouse = realEstateDetailData?.data?.realEstate?.businessType === BusinessType.RoomRent && (realEstateDetailData?.data?.relatedListings?.length ?? 0) > 0;
      const query = isRoomHouse ? buildRoomFilterQuery(Object.fromEntries(searchParams.entries())) : undefined;
      router.push(query ? `${fullRoute}?${query}` : fullRoute);
    };
    $[40] = getFullRouteForDetail;
    $[41] = realEstateDetailData?.data?.realEstate?.businessType;
    $[42] = realEstateDetailData?.data?.relatedListings?.length;
    $[43] = router;
    $[44] = searchParams;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const handleMaximizeDialog = t21;
  let t22;
  if ($[46] !== dialogContextItems || $[47] !== isMobile || $[48] !== openModal || $[49] !== openModalId) {
    t22 = {
      isMobile,
      openModal,
      openModalId,
      dialogContextItems
    };
    $[46] = dialogContextItems;
    $[47] = isMobile;
    $[48] = openModal;
    $[49] = openModalId;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const modalStateValue = t22;
  let t23;
  if ($[51] !== persistedModals) {
    t23 = {
      persistedModals
    };
    $[51] = persistedModals;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const persistedStateValue = t23;
  let t24;
  if ($[53] !== handleCloseDialog || $[54] !== handleDismissDialog || $[55] !== handleMaximizeDialog || $[56] !== handleMinimizeDialog || $[57] !== handleOnOpenChange || $[58] !== handleOpenDialog || $[59] !== handleRestoreDialog) {
    t24 = {
      handleOpenDialog,
      handleCloseDialog,
      handleRestoreDialog,
      handleMinimizeDialog,
      handlePersistDialog,
      handleClosePill,
      handleUnpersistDialogs,
      handleMaximizeDialog,
      handleOnOpenChange,
      handleDismissDialog
    };
    $[53] = handleCloseDialog;
    $[54] = handleDismissDialog;
    $[55] = handleMaximizeDialog;
    $[56] = handleMinimizeDialog;
    $[57] = handleOnOpenChange;
    $[58] = handleOpenDialog;
    $[59] = handleRestoreDialog;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  const actionsValue = t24;
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DetailDialogContainer data-sentry-element="DetailDialogContainer" data-sentry-source-file="DialogProvider.tsx" />;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== children || $[63] !== persistedStateValue) {
    t26 = <DialogDetailsPersistedStateContext.Provider value={persistedStateValue} data-sentry-element="DialogDetailsPersistedStateContext.Provider" data-sentry-source-file="DialogProvider.tsx">{t25}{children}</DialogDetailsPersistedStateContext.Provider>;
    $[62] = children;
    $[63] = persistedStateValue;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== modalStateValue || $[66] !== t26) {
    t27 = <DialogDetailsModalStateContext.Provider value={modalStateValue} data-sentry-element="DialogDetailsModalStateContext.Provider" data-sentry-source-file="DialogProvider.tsx">{t26}</DialogDetailsModalStateContext.Provider>;
    $[65] = modalStateValue;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== actionsValue || $[69] !== t27) {
    t28 = <DialogDetailsActionsContext.Provider value={actionsValue} data-sentry-element="DialogDetailsActionsContext.Provider" data-sentry-component="DialogDetailsProvider" data-sentry-source-file="DialogProvider.tsx">{t27}</DialogDetailsActionsContext.Provider>;
    $[68] = actionsValue;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  return t28;
}
export function useDialogDetails() {
  const $ = _c(3);
  const state = useDialogDetailsState();
  const actions = useDialogDetailsActions();
  let t0;
  if ($[0] !== actions || $[1] !== state) {
    t0 = {
      ...state,
      ...actions
    };
    $[0] = actions;
    $[1] = state;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function useDialogDetailsState() {
  const $ = _c(3);
  const modal = useDialogDetailsModalState();
  const persisted = useDialogDetailsPersistedState();
  let t0;
  if ($[0] !== modal || $[1] !== persisted) {
    t0 = {
      ...modal,
      ...persisted
    };
    $[0] = modal;
    $[1] = persisted;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function useDialogDetailsModalState() {
  const context = useContext(DialogDetailsModalStateContext);
  if (!context) {
    throw new Error("useDialogDetailsModalState must be used within a DialogDetailsProvider");
  }
  return context;
}
export function useDialogDetailsPersistedState() {
  const context = useContext(DialogDetailsPersistedStateContext);
  if (!context) {
    throw new Error("useDialogDetailsPersistedState must be used within a DialogDetailsProvider");
  }
  return context;
}
export function useDialogDetailsActions() {
  const context = useContext(DialogDetailsActionsContext);
  if (!context) {
    throw new Error("useDialogDetailsActions must be used within a DialogDetailsProvider");
  }
  return context;
}